import api, { getApiErrors } from '@app/services/api';
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import useApp from '../useApp';
import type {
    ApiError,
    UserAddParams,
    UserAddResponse,
    UserEditParams,
    UserEditResponse,
    UserDeleteParams,
    UserDeleteResponse,
    ClientListParams,
    LoginResponse,
} from '@app/services/api.types';
import { fn } from '@app/utils';

export const createUser = async (params: UserAddParams) => {
    const url = apiEndpoints.users.add;
    const response = await api.post<UserAddResponse>(url, params);
    return response?.data?.data;
};

export const updateUser = async (params: UserEditParams) => {
    const url = apiEndpoints.users.update(params.id);
    const response = await api.put<UserEditResponse>(url, params);
    return response?.data?.data;
};

export const removeUser = async (params: UserDeleteParams) => {
    const url = apiEndpoints.users.delete(params.id);
    const response = await api.delete<UserDeleteResponse>(url);
    return response?.data?.data;
};

export const exportUsersList = async (params: ClientListParams) => {
    const url = apiEndpoints.users.getListExport(params);
    const response = await api.get<Blob>(url, {
        responseType: 'blob',
    });
    return response;
};

export const switchUser = async (params: { id: string }): Promise<LoginResponse> => {
    const url = apiEndpoints.users.switch(params.id);
    const response = await api.post(url, params);
    return response?.data?.data;
};

export const switchBackUser = async () => {
    const response = await api.post(apiEndpoints.users.switchBack);
    return response?.data;
};

const useUserActions = () => {
    const queryClient = useQueryClient();
    const { message } = useApp();

    const addUser = useMutation<UserAddResponse['data'], AxiosError<ApiError>, UserAddParams>({
        mutationFn: createUser,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'User added successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [['user', 'list']],
        },
    });

    const editUser = useMutation<UserEditResponse['data'], AxiosError<ApiError>, UserEditParams>({
        mutationFn: updateUser,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'User updated successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [['user', 'list']],
        },
    });

    const deleteUser = useMutation<
        UserDeleteResponse['data'],
        AxiosError<ApiError>,
        UserDeleteParams
    >({
        mutationFn: removeUser,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'User deleted successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [['user', 'list']],
        },
    });

    const exportUsers = useMutation<AxiosResponse<Blob>, AxiosError<ApiError>, ClientListParams>({
        mutationFn: exportUsersList,
        onSuccess: (res) => {
            const match = res.headers['content-disposition'].match(/filename\s*=\s*"(.+)"/i);
            const filename = match?.[1] || 'users.csv';
            fn.downloadFileFromBlob({
                blob: res.data,
                fileName: filename,
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
    });

    const impersonateUser = useMutation({
        mutationFn: switchUser,
        onSuccess: (res) => {
            if (res?.token) {
                fn.storeImpersonationToken(res.token, { lifespan: res.expires_in ?? 3600 });
                message.success({
                    type: 'success',
                    content: 'Client impersonation started',
                });
                queryClient.invalidateQueries({ queryKey: ['dashboard'] });
                queryClient.invalidateQueries({ queryKey: ['interaction'] });
                queryClient.invalidateQueries({ queryKey: ['contact'] });
            }
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
    });

    const endImpersonateUser = useMutation({
        mutationFn: switchBackUser,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Client impersonation ended',
            });
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['dashboard'] });
                queryClient.invalidateQueries({ queryKey: ['interaction'] });
                queryClient.invalidateQueries({ queryKey: ['contact'] });
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
    });

    return {
        addUser,
        editUser,
        deleteUser,
        exportUsers,
        impersonateUser,
        endImpersonateUser,
    };
};

export default useUserActions;
